import { useMemo } from "react";
import { Button, Tag } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import CommonTable from "../../components/commonTable";
import {
  useGetMyWalletQuery, useGetMyWalletTransactionsQuery,
  useGetWalletByEmployeeQuery, useGetEmployeeWalletTransactionsQuery,
} from "../../services/wallet";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface TransactionRecord {
  key:             string;
  date?:           string;
  description?:    string;
  credit?:         number;
  debit?:          number;
  closingBalance?: number;
}

/* ─── Component ──────────────────────────────────────────────────────── */

const renderDateTime = (d?: string) => {
  if (!d) return '-';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return (
    <div>
      <div>{dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
      <div style={{ fontSize: 12, opacity: 0.65 }}>{dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
    </div>
  );
};

// Negative → red, positive → green, matching the "minus red / plus green" rule.
const renderSignedAmount = (v?: number) => {
  if (v === undefined || v === null) return '-';
  const sign = v < 0 ? '-' : '';
  return (
    <span style={{ color: v < 0 ? '#ff4d4f' : v > 0 ? '#52c41a' : undefined }}>
      {sign}₹{Math.abs(v).toLocaleString('en-IN')}
    </span>
  );
};

const WalletTransactions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Reached from the admin /wallet list with ?employeeId=... → that employee's
  // wallet; reached from the header wallet chip with no param → the logged-in
  // employee's own wallet ("me").
  const employeeId = searchParams.get('employeeId') || undefined;

  const { data: myWalletResp, isFetching: myWalletFetching } = useGetMyWalletQuery(undefined, { skip: !!employeeId });
  const { data: employeeWalletResp, isFetching: employeeWalletFetching } = useGetWalletByEmployeeQuery(
    employeeId as string, { skip: !employeeId }
  );
  const myWallet = employeeId
    ? (employeeWalletResp as any)?.data ?? employeeWalletResp
    : (myWalletResp as any)?.data ?? myWalletResp;
  const walletFetching = employeeId ? employeeWalletFetching : myWalletFetching;

  const { data: myTransactionsResp, isFetching: myTxFetching } = useGetMyWalletTransactionsQuery(undefined, { skip: !!employeeId });
  const { data: employeeTransactionsResp, isFetching: employeeTxFetching } = useGetEmployeeWalletTransactionsQuery(
    employeeId as string, { skip: !employeeId }
  );
  const transactionsResp     = employeeId ? employeeTransactionsResp : myTransactionsResp;
  const transactionsFetching = employeeId ? employeeTxFetching : myTxFetching;

  const transactions = useMemo<TransactionRecord[]>(() => {
    const rows: any[] = (transactionsResp as any)?.data || [];
    return rows.map((t: any) => {
      // Debit remarks already name the recipient (e.g. "Vendor payment -
      // Naveen"); credit remarks don't say who added it, so append that.
      let description = t.remark || t.description || '-';
      description = description.replace(/^Amount added to existing wallet$/i, 'Wallet top-up');
      if (t.type === 'credit' && t.createdBy?.name) {
        description = `${description} by ${t.createdBy.name}`;
      }
      if (t.vendorMobile) {
        description = `${description} (${t.vendorMobile})`;
      }
      return {
        key:            t._id,
        date:           t.transactionDate || t.dateTime || t.createdAt || t.date,
        description,
        credit:         t.credit,
        debit:          t.debit,
        closingBalance: t.closingBalance ?? t.balanceAfter,
      };
    });
  }, [transactionsResp]);

  const columns = [
    { title: 'Date',        dataIndex: 'date',        key: 'date',        render: renderDateTime },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { title: 'Credit',      dataIndex: 'credit', key: 'credit',
      render: (v?: number) => renderSignedAmount(v) },
    { title: 'Debit',       dataIndex: 'debit',  key: 'debit',
      render: (v?: number) => renderSignedAmount(v !== undefined ? -v : undefined) },
    { title: 'Closing Balance', dataIndex: 'closingBalance', key: 'closingBalance', render: renderSignedAmount },
  ];

  return (
    <div className="wallet-transactions-page">
      <div className="header-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
          <h2 style={{ margin: 0 }}>Wallet Transactions</h2>
        </div>
        <Tag color={myWallet?.isActive ? 'green' : 'red'} style={{ fontSize: 14, padding: '4px 12px' }}>
          Balance: ₹{(myWallet?.walletAmount ?? 0).toLocaleString('en-IN')}
        </Tag>
      </div>

      <div className="wallet-transactions-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={transactions}
          loading={walletFetching || transactionsFetching}
          pagination={false}
          rowKey={(record: TransactionRecord) => record.key}
        />
      </div>
    </div>
  );
};

export default WalletTransactions;
